import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../api";
import Layout from "../components/Layout";
import Swal from "sweetalert2";
import {
  buildErrorHtml,
  isBlank,
  isEmail,
  isPhone,
} from "../utils/validation";
import { getStepLabel } from "../utils/follow-up-steps";

export default function StudentDetails() {

  const { id } = useParams();

  const [student, setStudent] = useState(null);
  const [responsibles, setResponsibles] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [followUp, setFollowUp] = useState(null);
  const [activeTab, setActiveTab] = useState("dados");

  const [showResponsibleModal, setShowResponsibleModal] = useState(false);
  const [editingResponsible, setEditingResponsible] = useState(null);

  const [responsibleForm, setResponsibleForm] = useState({
    name: "",
    relationship: "",
    phone: "",
    email: "",
    address: "",
    primaryContact: false
  });

  const fetchData = useCallback(async () => {
    try {

      const studentRes = await api.get(`/students/${id}`);
      const respRes = await api.get(`/responsibles/student/${id}`);
      const absRes = await api.get(`/absences/student/${id}`);
      const followRes = await api.get(`/followups/student/${id}`);
      const followData = Array.isArray(followRes.data)
        ? (followRes.data.find((item) => item.status === "IN_PROGRESS")
            || followRes.data[0]
            || null)
        : (followRes.data || null);

      setStudent(studentRes.data);
      setResponsibles(respRes.data);
      setAbsences(absRes.data);
      setFollowUp(followData);

    } catch (error) {
      Swal.fire("Erro", "Não foi possível carregar os dados.", "error");
    }
  }, [id]);
  useEffect(() => {
    fetchData();
  }, [fetchData]);



  async function saveResponsible() {
    try {
      const errors = [];

      if (isBlank(responsibleForm.name)) {
        errors.push("Nome é obrigatório.");
      }

      if (isBlank(responsibleForm.relationship)) {
        errors.push("Parentesco é obrigatório.");
      }

      if (isBlank(responsibleForm.phone)) {
        errors.push("Telefone é obrigatório.");
      } else if (!isPhone(responsibleForm.phone)) {
        errors.push("Telefone inválido.");
      }

      if (!isBlank(responsibleForm.email) && !isEmail(responsibleForm.email)) {
        errors.push("Email inválido.");
      }

      if (errors.length > 0) {
        Swal.fire({
          icon: "warning",
          title: "Campos inválidos",
          html: buildErrorHtml(errors),
        });
        return;
      }

      if (editingResponsible) {
        await api.put(
          `/responsibles/${editingResponsible.id}`,
          responsibleForm
        );
      } else {
        await api.post("/responsibles", {
          ...responsibleForm,
          studentId: id
        });
      }

      setShowResponsibleModal(false);
      fetchData();

    } catch (error) {
      Swal.fire("Erro", "Erro ao salvar responsável.", "error");
    }
  }

  async function deleteResponsible(responsibleId) {

    const confirm = await Swal.fire({
      title: "Excluir responsável?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33"
    });

    if (!confirm.isConfirmed) return;

    await api.delete(`/responsibles/${responsibleId}`);
    fetchData();
  }

  function openWhatsApp(phone) {
    if (!phone) return;

    const cleanPhone = phone.replace(/\D/g, "");
    window.open(`https://wa.me/55${cleanPhone}`, "_blank");
  }

  if (!student) {
    return (
      <Layout>
        <p>Carregando...</p>
      </Layout>
    );
  }

  return (
    <Layout>

      <h4 className="mb-4">
        👨‍🎓 {student.name}
      </h4>

      {/* TABS */}
      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "dados" ? "active" : ""}`}
            onClick={() => setActiveTab("dados")}
          >
            Dados
          </button>
        </li>

        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "responsaveis" ? "active" : ""}`}
            onClick={() => setActiveTab("responsaveis")}
          >
            Responsáveis
          </button>
        </li>

        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "faltas" ? "active" : ""}`}
            onClick={() => setActiveTab("faltas")}
          >
            Faltas
          </button>
        </li>

        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "acompanhamento" ? "active" : ""}`}
            onClick={() => setActiveTab("acompanhamento")}
          >
            Acompanhamento
          </button>
        </li>
      </ul>

      {/* ================== DADOS ================== */}
      {activeTab === "dados" && (
        <div className="card shadow-sm p-4">
          <p><strong>Matrícula:</strong> {student.registrationNumber}</p>
          <p><strong>Telefone:</strong> {student.phone || "-"}</p>
          <p><strong>Turma:</strong> {student.class?.name}</p>
          <p><strong>Status:</strong> {getStudentStatusLabel(student.status)}</p>
        </div>
      )}

      {/* ================== RESPONSÁVEIS ================== */}
      {activeTab === "responsaveis" && (
        <div className="card shadow-sm p-4">

          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6>Responsáveis</h6>
            <button
              className="btn btn-sm btn-primary"
              onClick={() => {
                setEditingResponsible(null);
                setResponsibleForm({
                  name: "",
                  relationship: "",
                  phone: "",
                  email: "",
                  address: "",
                  primaryContact: false
                });
                setShowResponsibleModal(true);
              }}
            >
              + Novo Responsável
            </button>
          </div>

          {responsibles.length === 0 ? (
            <p>Nenhum responsável cadastrado.</p>
          ) : (
            responsibles.map(r => (
              <div key={r.id} className="card mb-2 shadow-sm">

                <div className="card-body d-flex justify-content-between align-items-center">

                  <div>
                    <strong>{r.name}</strong>

                    {r.primaryContact && (
                      <span className="badge bg-success ms-2">
                        Principal
                      </span>
                    )}

                    <div className="small text-muted">
                      {r.relationship} • {r.phone}
                    </div>
                  </div>

                  <div className="d-flex gap-2">

                    <button
                      className="btn btn-sm btn-success"
                      onClick={() => openWhatsApp(r.phone)}
                    >
                      💬
                    </button>

                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => {
                        setEditingResponsible(r);
                        setResponsibleForm(r);
                        setShowResponsibleModal(true);
                      }}
                    >
                      ✏️
                    </button>

                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => deleteResponsible(r.id)}
                    >
                      🗑
                    </button>

                  </div>

                </div>

              </div>
            ))
          )}

        </div>
      )}

      {/* ================== FALTAS ================== */}
      {activeTab === "faltas" && (
        <div className="card shadow-sm p-4">

          {absences.length === 0 ? (
            <p>Nenhuma falta registrada.</p>
          ) : (
            absences.map(a => (
              <div key={a.id} className="border rounded p-3 mb-2">
                <strong>
                  {new Date(a.date).toLocaleDateString()}
                </strong>
                <div>{a.reason || "Sem motivo informado"}</div>
              </div>
            ))
          )}

        </div>
      )}

      {/* ================== ACOMPANHAMENTO ================== */}
      {activeTab === "acompanhamento" && (
        <div className="card shadow-sm p-4">

          {!followUp ? (
            <p>Nenhum acompanhamento ativo.</p>
          ) : (
            <>
              <p><strong>Status:</strong> {getFollowUpStatusLabel(followUp.status)}</p>
              <p><strong>Etapa Atual:</strong> {getStepLabel(followUp.currentStep)}</p>
              <p><strong>Iniciado em:</strong> {new Date(followUp.createdAt).toLocaleDateString()}</p>
            </>
          )}

        </div>
      )}

      {/* ================== MODAL RESPONSÁVEL ================== */}
      {showResponsibleModal && (
        <div className="custom-modal-overlay">
          <div className="custom-modal">

            <h5>
              {editingResponsible ? "Editar Responsável" : "Novo Responsável"}
            </h5>

            <div className="mb-2">
              <label>Nome</label>
              <input
                className="form-control"
                value={responsibleForm.name}
                onChange={(e) =>
                  setResponsibleForm({
                    ...responsibleForm,
                    name: e.target.value
                  })
                }
              />
            </div>

            <div className="mb-2">
              <label>Parentesco</label>
              <input
                className="form-control"
                value={responsibleForm.relationship}
                onChange={(e) =>
                  setResponsibleForm({
                    ...responsibleForm,
                    relationship: e.target.value
                  })
                }
              />
            </div>

            <div className="mb-2">
              <label>Telefone</label>
              <input
                className="form-control"
                value={responsibleForm.phone}
                onChange={(e) =>
                  setResponsibleForm({
                    ...responsibleForm,
                    phone: e.target.value
                  })
                }
              />
            </div>

            <div className="form-check mt-2">
              <input
                type="checkbox"
                className="form-check-input"
                checked={responsibleForm.primaryContact}
                onChange={(e) =>
                  setResponsibleForm({
                    ...responsibleForm,
                    primaryContact: e.target.checked
                  })
                }
              />
              <label className="form-check-label">
                Contato Principal
              </label>
            </div>

            <div className="text-end mt-3">
              <button
                className="btn btn-secondary me-2"
                onClick={() => setShowResponsibleModal(false)}
              >
                Cancelar
              </button>

              <button
                className="btn btn-primary"
                onClick={saveResponsible}
              >
                Salvar
              </button>
            </div>

          </div>
        </div>
      )}

    </Layout>
  );
}




  function getFollowUpStatusLabel(status) {
    if (status === "IN_PROGRESS") return "Em andamento";
    if (status === "RESOLVED") return "Resolvido";
    if (status === "EVASION_CONFIRMED") return "Evasão confirmada";
    return status || "-";
  }

  function getStudentStatusLabel(status) {
    if (status === "ACTIVE") return "Ativo";
    if (status === "MONITORING") return "Monitoramento";
    if (status === "EVASION") return "Evasão";
    return status || "-";
  }
